import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Icon from 'react-feather';

import AdminHeader from '../../components/header/AdminHeader';
import CallexLoader from '../../components/CallexLoader';
import Pagination from '../../components/form/Pagination';
import ThreeSixtyCallDetailsModal from '../TenantAdmin/Call Report/ThreeSixtyCallDetailsModal';

import { agentThreeSixtyReportGet } from '../../store/slices/callHistorySlice';
import { useUserDetails } from '../../store/slices/authSlice';
import { useCrmDetails } from '../../store/slices/callingSlice';
import { formatCellValue, getMaskedValue, MASKED_NUMBER_FIELDS } from '../../utils/common';

// The grid's number column is "Destination Number"; the shared list only knows crm_number,
// so cover that name too.
const MASKED_FIELDS = [...MASKED_NUMBER_FIELDS, 'destination_number'];

// Agent side of the admin 360 Details Report, opened from the live-call menu. There is no
// filter: it is always built for the customer on the current call.
const LiveThreeSixtyDetailsReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useUserDetails();
  const crmDetails = useCrmDetails();
  const isNumberMaskingEnabled = user?.process_details?.numberMasking === 'true';
  const liveNumber = String(crmDetails?.[1]?.crmDetails?.primaryNumber || '').trim();

  // Starts true when a call is in progress so the empty state does not flash before
  // the first request begins.
  const [isLoading, setIsLoading] = useState(!!liveNumber);
  const [reportData, setReportData] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  // uuid of the call whose details popup is open.
  const [detailsUuid, setDetailsUuid] = useState(null);

  const fetchReportData = async (overrides = {}) => {
    if (!liveNumber) return;
    const payload = { ...pagination, ...overrides };

    setPagination(payload);
    setIsLoading(true);
    try {
      const response = await dispatch(
        agentThreeSixtyReportGet({ ...payload, destination_number: liveNumber })
      ).unwrap();
      setReportData(response || null);
    } catch (error) {
      setReportData(null);
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Also re-runs when the next call arrives while this page is still open.
  useEffect(() => {
    if (!liveNumber) {
      setReportData(null);
      return;
    }
    fetchReportData({ page: 1 });
  }, [liveNumber]);

  return (
    <div>
      <AdminHeader title={t('threeSixtyDetailsReport')} />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th className="text-center" style={{ width: '90px' }}>
                    {t('information')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        if (MASKED_FIELDS.includes(value?.name)) {
                          return (
                            <td key={idx}>
                              {getMaskedValue(val[value?.name], isNumberMaskingEnabled)}
                            </td>
                          );
                        }
                        return <td key={idx}>{formatCellValue(val[value?.name])}</td>;
                      })}
                      <td>
                        <div className="d-flex justify-content-center align-items-center">
                          {val?.uuid ? (
                            <Icon.Info
                              size={16}
                              className="cursor-pointer text-primary"
                              onClick={() => setDetailsUuid(val.uuid)}
                            />
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData?.total}
              limit={reportData?.limit || pagination.limit}
              page={reportData?.page || pagination.page}
              onLimitChange={(val) => fetchReportData({ limit: val, page: 1 })}
              onPageClick={(val) => fetchReportData({ page: val })}
              lastPageClick={(val) => fetchReportData({ page: val })}
              firstPageClick={(val) => fetchReportData({ page: val })}
              onRefreshClick={() => fetchReportData()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          {t('noDataAvailable')}
        </Card>
      )}
      {detailsUuid && (
        <ThreeSixtyCallDetailsModal
          isOpen={!!detailsUuid}
          handleCancel={() => setDetailsUuid(null)}
          uuid={detailsUuid}
          isAgent
        />
      )}
    </div>
  );
};

export default LiveThreeSixtyDetailsReport;
